/* @flow */

import { Formik, Form, Field } from 'formik';
import { compose } from 'ramda';
import * as React from 'react';
import { intlShape, injectIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import * as Yup from 'yup';

import styles from 'src/shared/app/authentication/component/base/AuthenticationBase.style';
import AuthenticationFormHeader from 'src/shared/app/authentication/component/base/AuthenticationFormHeader';
import authMessages from 'src/shared/app/authentication/l10n/authenticationL10n';
import messages from 'src/shared/app/authentication/l10n/forgotPasswordL10n';
import { usernameFieldMaxLength } from 'src/shared/app/authentication/settings/authenticationSettings';
import FormikFormGroup from 'src/shared/app/base/component/data-entry/form/FormikFormGroup';
import SecondaryButton from 'src/shared/ui/component/button/SecondaryButton';
import DiscreteLink from 'src/shared/ui/component/link/DiscreteLink';

type Props = {
  username: ?string,
  handleSubmit: (Object) => Promise<any>,
  handleHelpClick: () => void,
  // Self passed
  // $FlowIssue
  intl: intlShape,
};

const useStyles = createUseStyles(styles);

const ForgotPasswordFormDumb = ({
  username,
  handleSubmit,
  handleHelpClick,
  intl,
}: Props): React.Node => {
  const schema = Yup.object().shape({
    username: Yup.string().required().email(),
  });

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <Formik
      initialValues={{ username: username || '' }}
      validationSchema={schema}
      validateOnBlur={true}
      onSubmit={(values, { setSubmitting, setStatus }) =>
        handleSubmit(values).catch(({ errors }) => {
          setSubmitting(false);

          const { root, username } = errors || {};

          if (root || username) {
            setStatus(root || username);
          }
        })
      }
    >
      {({ status, values, isSubmitting }) => {
        const helperElt = status && (
          <div className={classes.support}>
            <DiscreteLink href="#" handleClick={handleHelpClick}>
              {intl.formatMessage(authMessages.contact)}
            </DiscreteLink>
          </div>
        );

        return (
          <Form className={classes.form} noValidate>
            <AuthenticationFormHeader withError={!!status}>
              {status || intl.formatMessage(messages.legend)}
            </AuthenticationFormHeader>
            <Field
              id="username"
              name="username"
              type="email"
              component={FormikFormGroup}
              componentOpts={{
                label: intl.formatMessage(authMessages.emailLabel),
              }}
              maxLength={usernameFieldMaxLength}
              required
              autoFocus
            />
            <div className={classes.submit}>
              <SecondaryButton
                isDisabled={!values.username}
                isSubmitting={isSubmitting}
              >
                {intl.formatMessage(messages.submit)}
              </SecondaryButton>
            </div>
            {helperElt}
          </Form>
        );
      }}
    </Formik>
  );
};

export default compose(React.memo, injectIntl)(ForgotPasswordFormDumb);
